import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { UitkTertiaryButton, UitkPrimaryButton } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { classNames, liveAnnounce, isElementVisible } from 'uitk-react-utils';
import styles from './uitk-date-picker.css';
import { UitkDatePickerInput } from './uitk-date-picker-input.esm.js';
import { UitkCalendar } from './uitk-calendar.esm.js';

/* eslint-disable react/no-unused-prop-types */
var getClassNameList = classNames.bind(styles);
var DatePickerMenuView = /** @class */ (function (_super) {
    __extends(DatePickerMenuView, _super);
    function DatePickerMenuView(props) {
        var _this = _super.call(this, props) || this;
        _this.elementRef = React.createRef();
        _this.state = {
            displayDate: undefined,
            localizedText: {
                cancelButton: function () { return props.l10n.formatText('datePicker.cancelButton.a11yLabel'); },
                datePickerOpened: function () { return props.l10n.formatText('datePicker.datePickerOpened.liveAnnounce'); },
                doneButtonFare: function (nights, lowestFareMessage, startDate, endDate) {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonFareWithSummary.single.a11yLabel', startDate, lowestFareMessage);
                    }
                    return props.l10n.formatText('datePicker.doneButtonFareWithSummary.multiple.a11yLabel', startDate, endDate, lowestFareMessage);
                },
                clearInstructions: function () { return props.l10n.formatText('datePicker.clearInstructions.a11yLabel'); },
                doneInstructions: function () { return props.l10n.formatText('datePicker.doneInstructions.a11yLabel'); },
                hiddenDoneButton: function (nights, startDate, endDate) {
                    if (nights <= 1) {
                        return props.l10n.formatText('datePicker.doneButtonWithSummary.single.a11yLabel', startDate);
                    }
                    return props.l10n.formatText('datePicker.doneButtonWithSummary.multiple.a11yLabel', startDate, endDate);
                }
            }
        };
        _this.onClickTopStart = _this.onClickTopStart.bind(_this);
        _this.onClickTopEnd = _this.onClickTopEnd.bind(_this);
        _this.onSubmitDates = _this.onSubmitDates.bind(_this);
        _this.onChangeMonth = _this.onChangeMonth.bind(_this);
        _this.onChangeStartDate = _this.onChangeStartDate.bind(_this);
        _this.onChangeEndDate = _this.onChangeEndDate.bind(_this);
        _this.clearDisplayDate = _this.clearDisplayDate.bind(_this);
        _this.focusOnElement = _this.focusOnElement.bind(_this);
        _this.hiddenDoneButton = _this.hiddenDoneButton.bind(_this);
        return _this;
    }
    DatePickerMenuView.prototype.componentDidMount = function () {
        var _a = this.props, formattedStartSelection = _a.formattedStartSelection, labels = _a.labels, preventAutoFocus = _a.preventAutoFocus, startSelectedMsg = _a.startSelectedMsg;
        var localizedText = this.state.localizedText;
        if (this.elementRef && this.elementRef.current) {
            var startInputRef = this.elementRef.current.getElementsByTagName('button')[0];
            if (!preventAutoFocus) {
                this.focusOnElement(startInputRef);
            }
        }
        var datePickerOpenedString = localizedText.datePickerOpened();
        liveAnnounce("".concat(datePickerOpenedString, ", \n      ").concat(formattedStartSelection, " ").concat(labels.start, " ").concat(startSelectedMsg));
    };
    DatePickerMenuView.prototype.shouldComponentUpdate = function (nextProps) {
        var selection = this.props.selection;
        if (nextProps && nextProps.holidays && nextProps.holidays.length > 0) {
            selection.configureHolidays(nextProps.holidays);
        }
        return true;
    };
    DatePickerMenuView.prototype.onClickTopStart = function () {
        var _a = this.props, onSelectStartMode = _a.onSelectStartMode, selection = _a.selection;
        this.setState({ displayDate: selection.start });
        onSelectStartMode();
    };
    DatePickerMenuView.prototype.onClickTopEnd = function () {
        var _a = this.props, onSelectEndMode = _a.onSelectEndMode, selection = _a.selection;
        this.setState({ displayDate: selection.end });
        onSelectEndMode();
    };
    DatePickerMenuView.prototype.onSubmitDates = function () {
        var _a = this.props, onSubmit = _a.onSubmit, onDoneButtonClickCB = _a.onDoneButtonClickCB;
        if (onDoneButtonClickCB) {
            var selection = this.props.selection;
            var start = selection.start, end = selection.end;
            onDoneButtonClickCB(start, end);
        }
        else {
            onSubmit();
        }
    };
    DatePickerMenuView.prototype.onChangeMonth = function (month) {
        var onChangeMonth = this.props.onChangeMonth;
        if (onChangeMonth) {
            onChangeMonth(month, false);
        }
    };
    DatePickerMenuView.prototype.onChangeStartDate = function (date) {
        var onChangeStartDate = this.props.onChangeStartDate;
        if (onChangeStartDate) {
            onChangeStartDate(date, false);
        }
    };
    DatePickerMenuView.prototype.onChangeEndDate = function (date) {
        var onChangeEndDate = this.props.onChangeEndDate;
        if (onChangeEndDate) {
            onChangeEndDate(date, false);
        }
    };
    DatePickerMenuView.prototype.clearDisplayDate = function () {
        this.setState({ displayDate: undefined });
    };
    DatePickerMenuView.prototype.focusOnElement = function (ref) {
        if (isElementVisible(ref)) {
            ref.focus();
        }
    };
    DatePickerMenuView.prototype.hiddenDoneButton = function () {
        var _a = this.props, formattedStartSelection = _a.formattedStartSelection, formattedEndSelection = _a.formattedEndSelection, selection = _a.selection;
        var localizedText = this.state.localizedText;
        var nights = selection.numberOfNights;
        var visuallyHiddenText = localizedText.hiddenDoneButton(nights, formattedStartSelection, formattedEndSelection);
        return (React.createElement("button", { type: "button", className: getClassNameList('is-visually-hidden'), onClick: this.onSubmitDates }, visuallyHiddenText));
    };
    DatePickerMenuView.prototype.render = function () {
        var _a = this.props, buttonText = _a.buttonText, clearButtonText = _a.clearButtonText, datePickerContent = _a.datePickerContent, doneAnalytics = _a.doneAnalytics, doubleMonths = _a.doubleMonths, endSelectedMsg = _a.endSelectedMsg, firstDayOfWeek = _a.firstDayOfWeek, firstValidDate = _a.firstValidDate, footerText = _a.footerText, formattedEndSelection = _a.formattedEndSelection, formattedStartSelection = _a.formattedStartSelection, holidays = _a.holidays, labels = _a.labels, lastValidDate = _a.lastValidDate, nextPageAnalytics = _a.nextPageAnalytics, onClearDates = _a.onClearDates, previousPageAnalytics = _a.previousPageAnalytics, selection = _a.selection, selectionClassNames = _a.selectionClassNames, singleMode = _a.singleMode, startSelectedMsg = _a.startSelectedMsg, submitDisabled = _a.submitDisabled, updateDatePicker = _a.updateDatePicker, useClearDates = _a.useClearDates;
        var _b = this.state, localizedText = _b.localizedText, displayDate = _b.displayDate;
        var monthsToRender = doubleMonths ? 2 : 1;
        var isDateSelected = singleMode ? selection.start : selection.start && selection.end;
        return (React.createElement("div", { className: getClassNameList('uitk-date-picker', 'date-picker-menu'), ref: this.elementRef },
            React.createElement(UitkLayoutFlex, { alignItems: "center", className: getClassNameList('uitk-date-picker-selection-bar', 'uitk-date-picker-selection-bar-top'), tag: "section" },
                React.createElement(UitkLayoutFlexItem, null,
                    React.createElement(UitkDatePickerInput, { cssClassNames: selectionClassNames(true), isDialog: false, onClick: this.onClickTopStart, selectionDate: formattedStartSelection, selectionDateMsg: "".concat(labels.start, " ").concat(startSelectedMsg), selection: selection.start, singleMode: singleMode })),
                !singleMode && (React.createElement(UitkLayoutFlexItem, { className: getClassNameList('uitk-date-picker-selection-icon') },
                    React.createElement(UitkIcon, { name: "arrow_forward", size: UitkIconSize.SMALL }))),
                !singleMode && (React.createElement(UitkLayoutFlexItem, null,
                    React.createElement(UitkDatePickerInput, { cssClassNames: selectionClassNames(false), isDialog: false, onClick: this.onClickTopEnd, selectionDate: formattedEndSelection, selectionDateMsg: "".concat(labels.end, " ").concat(endSelectedMsg), selection: selection.end }))),
                this.hiddenDoneButton()),
            React.createElement(UitkCalendar, { buttonText: buttonText, datePickerContent: datePickerContent, firstMonth: selection.start || firstValidDate, monthsToRender: monthsToRender, isDialog: false, selection: selection, selectionStart: selection.start, selectionEnd: selection.end, startDate: firstValidDate, endDate: lastValidDate, updateDatePicker: updateDatePicker, displayDate: displayDate, clearDisplayDate: this.clearDisplayDate, firstDayOfWeek: firstDayOfWeek, singleMode: singleMode, nextPageAnalytics: nextPageAnalytics, previousPageAnalytics: previousPageAnalytics, holidays: holidays, onChangeMonth: this.onChangeMonth, onChangeStartDate: this.onChangeStartDate, onChangeEndDate: this.onChangeEndDate }),
            React.createElement(UitkLayoutFlex, { className: getClassNameList('uitk-date-picker-menu-footer') },
                (footerText || (selection.holidaysConfigured && selection.holidaysName !== '')) && [
                    React.createElement(UitkLayoutFlexItem, { key: "0", className: getClassNameList('uitk-date-picker-footer-text') },
                        React.createElement("div", null,
                            selection.holidaysConfigured && selection.holidaysName !== '' && (React.createElement("p", { key: "holiday" }, selection.holidaysName)),
                            footerText && React.createElement("p", { key: "legal" }, footerText)))
                ],
                React.createElement(UitkLayoutFlexItem, { className: getClassNameList('dialog-done'), shrink: 0 },
                    React.createElement("div", null,
                        useClearDates && (React.createElement(UitkSpacing, { margin: { inline: 'three' } },
                            React.createElement(UitkTertiaryButton, { "aria-label": localizedText.clearInstructions(), disabled: !isDateSelected, onClick: onClearDates, "data-stid": "clear-date-picker" }, clearButtonText))),
                        React.createElement(UitkPrimaryButton, { "aria-label": localizedText.doneInstructions(), analytics: doneAnalytics, onClick: this.onSubmitDates, "data-stid": "apply-date-picker", disabled: submitDisabled }, buttonText))))));
    };
    DatePickerMenuView.displayName = 'UitkDatePickerMenuView';
    return DatePickerMenuView;
}(React.Component));
var UitkDatePickerMenuView = function (props) {
    var l10n = useLocalization();
    return React.createElement(DatePickerMenuView, __assign({ l10n: l10n }, props));
};
UitkDatePickerMenuView.displayName = 'UitkDatePickerMenuView';
var UitkNewDatePickerMenuView = UitkDatePickerMenuView;

export { UitkDatePickerMenuView, UitkNewDatePickerMenuView };
